/**
 * utils/branches.js
 * Pure functions for managing the cabang branches collection (multi-branch cabang feature).
 * `data.branches`: { id, name, bankAccount, createdAt }[] — `name` is the only required field
 * (trimmed, non-empty, unique case-insensitively). No `code` field — unlike salespeople, a
 * branch never feeds a minted invoice number, so it needs no short code. Mirrors
 * utils/contacts.js's shape and refcount-guarded delete.
 *
 * **`bankAccount`** (per-location bank account, added alongside moving branch management into
 * Pengaturan): free text, may be absent/empty — never validated, reformatted, or normalised
 * (an account number must print one day exactly as typed, same rule as Contact.npwp). Set
 * directly on `addBranch`/`updateBranch`'s own `fields`, never copied from or compared against
 * another location's account — two locations may share identical details or differ completely,
 * that's the user's business. **Pusat has no branch record** (it's `branchId: null`
 * everywhere in this app, never a row in this collection), so its own account lives on
 * `data.settings.bankAccount` instead (alongside the company letterhead) — `bankAccountFor`
 * below is the one function that hides this storage split from every caller.
 */
import { generateId } from "./idGenerators";
import { DEFAULT_CABANG_NAME } from "./company";
import { bucketLabel } from "./transformations";

/** The 3 real branches confirmed with the client — used to seed BOTH a fresh install
 *  (seedData.js) and an upgrading save with no prior branches collection (storage.js's
 *  v15->v16 migration), so the two paths can never disagree on what a shop starts with. */
export const SEED_BRANCH_NAMES = ["MMC", "AMIN", "TMM"];

function norm(s) {
  return String(s || "").trim();
}

function nameCollides(branches, name, excludeId) {
  const key = norm(name).toLowerCase();
  return branches.some((b) => b.id !== excludeId && norm(b.name).toLowerCase() === key);
}

/**
 * Add a new branch. Same-reference no-op when `fields.name` is blank (after trim) or
 * duplicates an existing branch's name (case-insensitive). `fields.bankAccount` (free text) is
 * stored exactly as given — no trim, no default beyond "" when absent; never seeded from
 * another location's account.
 */
export function addBranch(data, fields) {
  const branches = data.branches || [];
  const name = norm(fields?.name);
  if (!name) return data;
  if (nameCollides(branches, name, null)) return data;

  const bankAccount = typeof fields?.bankAccount === "string" ? fields.bankAccount : "";
  const branch = { id: generateId(), name, bankAccount, createdAt: new Date().toISOString() };
  return { ...data, branches: [...branches, branch] };
}

/**
 * Update an existing branch's name and/or bank account. Same-reference no-op when `id` doesn't
 * resolve, the new name is blank, or it collides with ANOTHER branch's name (renaming to its
 * own current name is fine — that's not a collision with itself). `fields.bankAccount`, when a
 * string, replaces the stored value exactly as given (no trim/validation); omitting the key
 * leaves the current value untouched.
 */
export function updateBranch(data, id, fields) {
  const branches = data.branches || [];
  const idx = branches.findIndex((b) => b.id === id);
  if (idx === -1) return data;

  const current = branches[idx];
  const name = fields?.name != null ? norm(fields.name) : current.name;
  if (!name) return data;
  if (nameCollides(branches, name, id)) return data;

  const bankAccount = typeof fields?.bankAccount === "string" ? fields.bankAccount : (current.bankAccount || "");
  const next = { ...current, name, bankAccount };
  return { ...data, branches: branches.map((b, i) => (i === idx ? next : b)) };
}

/** Two-collection count, mirroring contactRefCount's own lesson (utils/contacts.js): a branch
 *  can be referenced from salespeople.branchId AND transactions.branchId — summing only one
 *  would wrongly allow deleting a branch still in use by the other. */
export function branchRefCount(data, id) {
  const salesCount = (data.salespeople || []).filter((s) => s.branchId === id).length;
  const txnCount = (data.transactions || []).filter((t) => t.branchId === id).length;
  // Biaya Lain-Lain / Pengeluaran booked at this branch (2026-10-11) is a reference too.
  const expenseCount = (data.expenses || []).filter((e) => e.branchId === id).length;
  return salesCount + txnCount + expenseCount;
}

/** Delete only when unreferenced (branchRefCount === 0). Same-reference no-op otherwise — the
 *  UI is expected to explain why via branchRefCount before offering delete. */
export function deleteBranch(data, id) {
  if (branchRefCount(data, id) > 0) return data;
  const branches = data.branches || [];
  const next = branches.filter((b) => b.id !== id);
  if (next.length === branches.length) return data;
  return { ...data, branches: next };
}

/** Display label for a branch: just its name (no secondary detail — unlike a contact's city,
 *  a branch has nothing else to disambiguate with). */
export function displayBranch(b) {
  return b.name;
}

/** Resolve a branchId to its current name, or null when missing/unresolvable (an old
 *  transaction predating this feature, or a since-deleted branch). Returns null rather than a
 *  baked-in fallback so each caller picks its own: the print layer (Penjualan.js) converts
 *  null -> undefined so InvoiceCabang.js's OWN existing DEFAULT_CABANG_NAME default parameter
 *  fires unchanged; the dashboard badge (Penjualan.js/TransactionDetailModal) falls back to
 *  the generic "Cabang" bucket label instead — asserting a SPECIFIC branch name for data this
 *  app deliberately never backfilled with one would be dishonest, not just imprecise. */
export function resolveBranchName(branches, branchId) {
  const b = (branches || []).find((x) => x.id === branchId);
  return b ? b.name : null;
}

/** The Lokasi-badge formula every list page/detail modal already computed inline, extracted
 *  here (Pembelian batch 2, transfer support) because it's needed as a genuinely NEW value in
 *  its own right, not just for de-duplication: `txn.location`/`txn.branchId` on a TRANSFER
 *  always encode the SOURCE side (see the Transaction data-model entry, root CLAUDE.md), so
 *  this same expression doubles as "the transfer's source label" wherever a perspective OTHER
 *  than the source's own books needs to name it (Pembelian's own Penjual column, and the two
 *  detail modals' Pihak field when opened from Pembelian) — see those callers' own comments.
 *  Resolves the real branch name when `location === "CABANG"` and it's resolvable, else the
 *  generic Pusat/Cabang bucket label — byte-identical to the ad-hoc expression every existing
 *  Lokasi badge already used before this extraction, so lifting those call sites to it later is
 *  a pure no-op refactor, not a behavior change (not done in this same batch — see that batch's
 *  own CLAUDE.md note on why the pre-existing, already-correct call sites were left alone). */
export function resolveLocationLabel(location, branchId, branches) {
  return (location === "CABANG" ? resolveBranchName(branches, branchId) : null) || bucketLabel(location);
}

/** The single canonical bank-account lookup for a location — `branchId: null` means Pusat,
 *  the same convention `defaultBranchId`/every salesperson/transaction anchor already uses.
 *  Pusat's account lives on `data.settings.bankAccount`; a Cabang branch's lives on its own
 *  `data.branches` record. Free text, may legitimately be empty/absent — returns "" rather than
 *  null/undefined so a caller never needs its own separate "nothing set" check. Every reader of
 *  a location's bank account should go through this function rather than reaching into
 *  `settings`/`branches` directly, so the Pusat/Cabang storage split never leaks into calling
 *  code (this app has already had a near miss from exactly that kind of leak elsewhere). Never
 *  copies or compares one location's account against another's — two locations may share
 *  identical details or differ completely, that's the user's business, not this function's. */
export function bankAccountFor(data, branchId) {
  if (branchId == null) return (data.settings && data.settings.bankAccount) || "";
  const b = (data.branches || []).find((x) => x.id === branchId);
  return (b && b.bankAccount) || "";
}

/** The branch a new salespeople-related record should default to when nothing else picked
 *  one: the branch named DEFAULT_CABANG_NAME ("MMC") if present — the same branch the v15->v16
 *  migration backfills existing salespeople to — else the first branch in list order, else
 *  null (only possible if the branches collection itself is empty). Centralized so the
 *  migration, the inline "+ Tambah sales baru" quick-add, and Kelola Sales' own add panel
 *  can't independently disagree. */
export function defaultBranchId(branches) {
  const list = branches || [];
  const mmc = list.find((b) => b.name === DEFAULT_CABANG_NAME);
  return (mmc || list[0] || { id: null }).id;
}

/** Build the 3 real seed branches — used by BOTH storage.js's v15->v16 migration (upgrade)
 *  and seedData.js's buildSeedData (fresh install). Pure; fresh id/timestamp per call. */
export function seedBranches() {
  return SEED_BRANCH_NAMES.map((name) => ({ id: generateId(), name, createdAt: new Date().toISOString() }));
}
